import i18n from 'i18n-js';
import { Platform, Linking } from 'react-native';

const openExternalUrl = (url, target = '', features = '') => {
  if (Platform.OS === 'web') {
    window.open(url, target, features);
  } else {
    Linking.openURL(url);
  }
};

const getPopupFeatures = () => {
  if (Platform.OS !== 'web') return '';
  return `width=${screen.availWidth},height=${screen.availHeight}`;
};

/**
 * Post-scan handler registry.
 * Each handler defines:
 *   - isInteractive: true if it requires user input (shows a UI prompt)
 *   - getConfig(order): returns UI config for the step (interactive only)
 *   - onSubmit / execute: action logic (used by thunks in scanpackScanningActions)
 *
 * To add a new post-scan option:
 *   1. Add an entry here with isInteractive flag
 *   2. If interactive, add the corresponding UI in ScanpackScanning/index.js
 *   3. No pipeline code changes needed
 */
export const POST_SCAN_HANDLERS = {
  Record: {
    isInteractive: true,
    getConfig: (order) => ({
      fieldLabel: 'Tracking Number Recording',
      messageDetail: i18n.t('recordTracingmsg.createShippingLabel'),
      verifyNoTracking: false
    })
  },
  Verify: {
    isInteractive: true,
    getConfig: (order) => {
      const hasTrackingNum =
        order?.tracking_num !== null &&
        order?.tracking_num !== undefined &&
        order?.tracking_num !== '';
      return {
        fieldLabel: 'Shipping Label Verification',
        messageDetail: hasTrackingNum
          ? i18n.t('shippinglabelmsg.scanShippingLabel')
          : i18n.t('shippinglabelmsg.noRecordTracking'),
        verifyNoTracking: !hasTrackingNum
      };
    }
  },
  Barcode: {
    isInteractive: false
  },
  PackingSlip: {
    isInteractive: false
  }
};

/**
 * Check if packing camera should be shown before post-scan options.
 * Matches old scanpack_item.js line 5797-5809.
 */
export const shouldShowPackingCam = (scanPackSettings, storeInfo) => {
  return (
    scanPackSettings?.packing_cam_enabled === true &&
    scanPackSettings?.camera_option === 'photo' &&
    scanPackSettings?.capture_image_option !== 'do_not_take_image' &&
    storeInfo?.disable_packing_cam === false
  );
};

/**
 * Validate tracking number against comma-separated prefix list.
 * Matches old tracking_submit.js lines 66-83.
 */
export const validateTrackingPrefix = (trackingNumber, prefixString) => {
  if (!prefixString) return true;
  const prefixes = prefixString.split(',').map(p => p.trim());
  return prefixes.some(prefix => trackingNumber.startsWith(prefix));
};

/**
 * Get config for a post-scan step.
 * Returns null if optionType is 'None' or not found in registry.
 */
export const getPostScanStepConfig = (optionType, order) => {
  const handler = POST_SCAN_HANDLERS[optionType];
  if (!handler || !handler.getConfig) return null;
  return handler.getConfig(order);
};

/**
 * Check if a post-scan option type is interactive (requires user input).
 */
export const isInteractiveOption = (optionType) => {
  const handler = POST_SCAN_HANDLERS[optionType];
  return handler?.isInteractive === true;
};

export const isShippingEasyOrder = (order, scanPackSettings) => {
  return (
    scanPackSettings?.return_to_orders === false &&
    order?.popup_shipping_label &&
    order?.store_type === 'ShippingEasy'
  );
};

export const openShippingEasyUrl = (shipmentId) => {
  const url = `http://app.shippingeasy.com/shipments/${parseInt(shipmentId)}/edit`;
  openExternalUrl(url, '', getPopupFeatures());
};

export const openShopifyPopup = (order) => {
  if (
    order?.open_shopify_create_shipping_label &&
    order?.store_type === 'Shopify' &&
    order?.store_order_id &&
    order?.shopify_store_name
  ) {
    const url = `https://admin.shopify.com/store/${order.shopify_store_name}/orders/${order.store_order_id}`;
    openExternalUrl(url, 'Groovepacker - Shopify Shipping Label', getPopupFeatures());
  }
};

export const getShippingLabelAction = (apiOrder, ssLabelData, generalSettings) => {
  if (!apiOrder?.use_api_create_label) return {type: 'none'};

  const labelData = ssLabelData?.ss_label_data;
  const isDirectPrinted = labelData?.direct_printed;

  if (apiOrder?.print_ss_label && !isDirectPrinted) {
    return {
      type: 'navigate_shipping_label',
      params: {
        directPrintingSetting: generalSettings?.print_ss_shipping_labels
      }
    };
  }

  if (generalSettings?.print_ss_shipping_labels && labelData?.direct_printed_url) {
    const dimensionParts = labelData?.dimensions
      ? labelData.dimensions.split('x')
      : ['4', '6'];
    return {
      type: 'direct_print',
      params: {
        printUrl: labelData.direct_printed_url,
        dimensions: {width: dimensionParts[0], height: dimensionParts[1]},
        settings: generalSettings
      }
    };
  }

  if (labelData?.direct_printed_url) {
    return {
      type: 'browser_open',
      params: {url: labelData.direct_printed_url}
    };
  }

  return {type: 'none'};
};

export const openOrderCupPopup = (order, generalSettings) => {
  if (order?.order_cup_direct_shipping) {
    const customFields = {
      custom_field_one: generalSettings?.custom_field_one,
      custom_field_two: generalSettings?.custom_field_two
    };
    const key = Object.keys(customFields).find(k => customFields[k] === 'UOID');
    if (key && order?.order_info?.[key]) {
      const url = `https://ship.ordercup.com/pick_and_pack/ship?uoid=${order.order_info[key]}`;
      openExternalUrl(url, 'Groovepacker - OrderCup Direct Shipping', getPopupFeatures());
    }
  }
};
